import Catalog16 from '@carbon/icons-react/es/catalog/16';
import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import ChevronUp16 from '@carbon/icons-react/es/chevron--up/16';
import Close16 from '@carbon/icons-react/es/close/16';
import ShareKnowledge16 from '@carbon/icons-react/es/share-knowledge/16';
import { Collapse, Space } from 'antd';
import { isEmpty, keyBy } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { PermissionSetType, UserType } from 'api/models';
import { View } from 'api/views/types';
import { Drawer } from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Radio } from 'components/atoms/radio';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { Switch } from 'components/atoms/switch/switch';
import { FilterDrawerWithTrigger } from 'components/molecules/filter-drawer';
import { Filter } from 'components/molecules/filters/types';
import { toastService } from 'components/molecules/toast';
import {
  ButtonGroup,
  DrawerContent,
} from 'components/organisms/task-pane/task-detail';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import {
  getApiFilterTransformer,
  to_filter,
} from 'components/pages/api-filter-transformer';
import { useUserPermissionSetType } from 'hooks/permissions';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { CancelSaveViewButton, SaveUpdateViewButton } from '../components';
import { CreatableView } from '../types';

const { Panel } = Collapse;

export const DrawerAtom = styled(Drawer).attrs({
  width: 360,
  placement: 'right',
  closeIcon: <Close16 />,
})`
  .ant-drawer-body {
    padding: 0;
  }
`;

const PanelHeader = styled.div``;

export const FormStyled = styled(Form)`
  padding: var(--spacing-05);
`;

export const FormItemStyled = styled(Form.Item)`
  margin-bottom: 0;
  padding-bottom: 0 !important;
`;

const FormItemConditions = styled(Form.Item)`
  padding-left: var(--spacing-06);

  .ant-tag {
    padding: 0 calc(var(--spacing-03) - var(--spacing-01));
  }
`;

const CollapseStyled = styled(Collapse)`
  border-top: 1px solid var(--border-color);

  .ant-collapse-header {
    color: var(--blue-60);
    padding-left: var(--spacing-06);
  }

  .ant-collapse-arrow {
    color: var(--blue-60) !important;
    top: var(--spacing-02) !important;
    left: var(--spacing-01) !important;
    height: var(--spacing-06);
  }

  .ant-collapse-content-box {
    padding: 0;
  }

  .ant-form-item {
    padding-bottom: 0;
  }
`;

const FormItemWithoutMargin = styled(Form.Item)`
  margin-bottom: 0;
  margin-top: var(--spacing-05);
`;

const SwitchText = styled(Space)`
  padding-left: var(--spacing-03);
`;

const CustomerPortalText = styled.span<{ allowTaskListInProject: boolean }>`
  padding-left: var(--spacing-03);
  color: ${({ allowTaskListInProject }) =>
    allowTaskListInProject ? 'var(--gray-100)' : 'var(--gray-50)'};
`;

const ViewIcon = styled.span`
  position: relative;
  top: var(--spacing-01);
  padding-left: var(--spacing-03);
`;

export const Content = styled(DrawerContent)`
  ${DrawerContent.Content} {
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
`;

interface Props<ViewDefinition = any> {
  visible: boolean;
  currentViewDefinition: ViewDefinition;
  onClose: () => void;
  view?: View<ViewDefinition>;
  onViewUpdate?: (view: View<ViewDefinition>) => Promise<View<ViewDefinition>>;
  onViewCreate?: (
    view: CreatableView<ViewDefinition>,
  ) => Promise<View<ViewDefinition>>;
  defaultSharedFilter?: Filter;
  isDashboardView?: boolean;
}

export const ManageViewDrawer: FC<Props> = ({
  visible,
  onClose,
  view,
  currentViewDefinition,
  onViewUpdate,
  onViewCreate,
  defaultSharedFilter,
  isDashboardView,
}) => {
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [viewName, setViewName] = useState('');
  const [hideViewInProjectsOptions, setHideViewInProjectsOptions] =
    useState(true);
  const [allowTaskListInProject, setAllowTaskListInProject] = useState(true);
  const [viewInCustomerPortal, setViewInCustomerPortal] = useState(false);
  const [createViewForAllProject, setCreateViewForAllProject] = useState(true);

  const editMode = view != null;
  const [form] = Form.useForm();
  const inputRef = useRef<any>(null);
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isCollaborator =
    currentUserPermissionSetType === PermissionSetType.Collaborator ||
    currentUserPermissionSetType ===
      PermissionSetType.AccountVendorCollaborator;
  const {
    user: { userId: currentUserId, userType: currentUserType },
  } = useUser();
  const translate = useTranslate();
  const viewPlaceHolder = isDashboardView ? 'filter' : 'view';

  const onFormValueChange = useCallback(() => {
    setSaveDisabled(isEmpty(viewName));
  }, [viewName]);

  const setFocus = useCallback(() => {
    if (visible) {
      inputRef.current.focus();
    }
    if (visible && inputRef.current && view?.viewName) {
      inputRef.current.setSelectionRange(
        view.viewName.length,
        view.viewName.length,
      );
      inputRef.current.focus();
    }
  }, [visible, view?.viewName]);

  const { fields } = useProjectFieldsAsFilterableFields();
  const allFields = useMemo(
    () => fields.filter((field) => field.fieldName !== 'teamMembers'),
    [fields],
  );

  const [filter, setFilter] = useState<Filter>(
    defaultSharedFilter || {
      criteria: [],
      type: QueryCombinerType.All,
    },
  );

  useEffect(() => {
    if (!defaultSharedFilter) {
      return;
    }
    setFilter(defaultSharedFilter);
  }, [defaultSharedFilter]);
  const transformer = useMemo(
    () => getApiFilterTransformer(keyBy(allFields, 'fieldName')),
    [allFields],
  );

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        viewName,
        self: hideViewInProjectsOptions,
        inProject: allowTaskListInProject,
        inCustomerPortal: viewInCustomerPortal,
        viewForAllProject: createViewForAllProject,
        createView: filter,
      });
      setSaveDisabled(isEmpty(viewName));
    } else {
      form.resetFields();
      setViewName('');
      setHideViewInProjectsOptions(true);
      setViewInCustomerPortal(false);
      setCreateViewForAllProject(true);
    }
  }, [
    visible,
    form,
    view,
    hideViewInProjectsOptions,
    allowTaskListInProject,
    viewInCustomerPortal,
    createViewForAllProject,
    viewName,
    filter,
  ]);

  useEffect(() => {
    if (view) {
      setViewName(view.viewName);
      setHideViewInProjectsOptions(view.self);
      setAllowTaskListInProject(
        view?.viewFilterShareConfig?.shareWithProjects ?? false,
      );
      setViewInCustomerPortal(
        view?.viewFilterShareConfig?.sharedWithCustomerPortal ?? false,
      );
      setCreateViewForAllProject(
        view?.viewFilterShareConfig?.shareWithAllProjects ?? false,
      );
    }
  }, [view]);

  useEffect(() => {
    if (!visible) {
      setFilter(
        defaultSharedFilter || {
          criteria: [],
          type: QueryCombinerType.All,
        },
      );
    }
    if (visible && view && view?.viewFilterShareConfig?.filter) {
      setFilter(transformer.from(view.viewFilterShareConfig.filter));
    }
  }, [visible, view, transformer, defaultSharedFilter]);

  const onViewEdit = useCallback(
    (formValues) => {
      onViewUpdate?.({
        ...view,
        self: formValues.self,
        viewName: formValues.viewName,
        viewFilterShareConfig: {
          sharedWithCustomerPortal: formValues.inCustomerPortal ?? false,
          shareWithProjects: formValues.inProject ?? false,
          shareWithAllProjects: formValues.viewForAllProject ?? false,
          filter: formValues.createView
            ? to_filter(formValues.createView)
            : ({
                nativeFields: [],
                customFields: [],
                match: 'all',
              } as SimpleSearchFilter),
        },
      } as View<any>)
        .then(() => {
          toastService({
            title: isDashboardView
              ? translate('filter.update.success.title')
              : translate('success'),
            message: translate(
              isDashboardView
                ? 'filter.update.success'
                : 'views.update.success',
              {
                viewName: formValues.viewName,
              },
            ),
          }).success();
          onClose();
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate(
              isDashboardView ? 'filter.update.failed' : 'views.update.failed',
              {
                viewName: formValues.viewName,
              },
            ),
          }).error();
        })
        .finally(() => setActionInProgress(false));
    },
    [isDashboardView, onClose, onViewUpdate, translate, view],
  );

  const onViewNewCreate = useCallback(
    (formValues) => {
      onViewCreate?.({
        self: formValues.self,
        viewName: formValues.viewName,
        viewFilterShareConfig: {
          sharedWithCustomerPortal: formValues.inCustomerPortal ?? false,
          shareWithProjects: formValues.inProject ?? false,
          shareWithAllProjects: formValues.viewForAllProject ?? false,
          filter: formValues.createView
            ? to_filter(formValues.createView)
            : ({
                nativeFields: [],
                customFields: [],
                match: 'all',
              } as SimpleSearchFilter),
        },
        viewDefinition: currentViewDefinition,
      })
        .then(() => {
          toastService({
            title: isDashboardView
              ? translate('filter.create.success.title')
              : translate('success'),
            message: translate(
              isDashboardView
                ? 'filter.create.success'
                : 'views.create.success',
              {
                viewName: formValues.viewName,
              },
            ),
          }).success();
          onClose();
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate(
              isDashboardView ? 'filter.create.failed' : 'views.create.failed',
              {
                viewName: formValues.viewName,
              },
            ),
          }).error();
        })
        .finally(() => setActionInProgress(false));
    },
    [currentViewDefinition, isDashboardView, onClose, onViewCreate, translate],
  );

  const onSave = useCallback(() => {
    const formValues = form.getFieldsValue();

    setActionInProgress(true);
    if (editMode) {
      onViewEdit(formValues);
    } else {
      onViewNewCreate(formValues);
    }
  }, [form, editMode, onViewEdit, onViewNewCreate]);

  const onToggleViewInAllProject = useCallback(() => {
    setAllowTaskListInProject(!allowTaskListInProject);

    if (allowTaskListInProject) {
      setViewInCustomerPortal(false);
    }

    if (!allowTaskListInProject && !(filter.criteria.length > 0)) {
      setCreateViewForAllProject(true);
    }
  }, [allowTaskListInProject, filter.criteria.length]);

  return (
    <DrawerAtom
      visible={visible}
      onClose={onClose}
      destroyOnClose
      afterVisibleChange={setFocus}
    >
      <Content>
        <DrawerContent.Title data-cy="timesheets.task-drawer.add_tasks">
          {editMode ? (
            <FormattedMessage id={`views.edit_${viewPlaceHolder}.title`} />
          ) : (
            <FormattedMessage id={`views.new_${viewPlaceHolder}.title`} />
          )}
        </DrawerContent.Title>
        <DrawerContent.Content>
          <FormStyled
            layout="vertical"
            onValuesChange={onFormValueChange}
            form={form}
          >
            <Form.Item
              label={<FormattedMessage id="views.view_name.label" />}
              name="viewName"
            >
              <Input
                data-cy="views.new-view.name"
                placeholder={useTranslation(
                  `views.new_${viewPlaceHolder}_name.placeholder`,
                )}
                autoFocus
                autoComplete="off"
                onChange={(e) => setViewName(e.target.value)}
                ref={inputRef}
              />
            </Form.Item>
            <Form.Item
              label={
                <FormattedMessage
                  id={`views.${viewPlaceHolder}_available.label`}
                />
              }
              name="self"
            >
              <RadioGroup
                onChange={(e) => setHideViewInProjectsOptions(e.target.value)}
              >
                <Space direction="vertical">
                  <Radio
                    data-cy="views.new-view.available.options.me"
                    value
                    disabled={
                      isCollaborator ||
                      (editMode && view.createdBy !== currentUserId)
                    }
                  >
                    <FormattedMessage id="views.view_available.options.me" />
                  </Radio>
                  <Radio
                    data-cy="views.new-view.available.options.team"
                    value={false}
                    disabled={
                      isCollaborator ||
                      (editMode && view.createdBy !== currentUserId)
                    }
                  >
                    <FormattedMessage id="views.view_available.options.team" />
                  </Radio>
                </Space>
              </RadioGroup>
            </Form.Item>
            {!hideViewInProjectsOptions &&
              !isDashboardView &&
              currentUserType !== UserType.Partner && (
                <CollapseStyled
                  bordered={false}
                  defaultActiveKey={
                    view?.viewFilterShareConfig?.shareWithProjects ? ['1'] : []
                  }
                  expandIcon={({ isActive }) =>
                    isActive ? <ChevronUp16 /> : <ChevronDown16 />
                  }
                  className="site-collapse-custom-collapse"
                  ghost
                >
                  <Panel
                    header={
                      <PanelHeader data-cy="views.new-view.advanced-sharing-options">
                        <FormattedMessage id="views.view.advanced_sharing_options" />
                      </PanelHeader>
                    }
                    key="1"
                    className="site-collapse-custom-panel"
                  >
                    <>
                      <FormItemStyled
                        label={
                          <FormattedMessage id="views.view_available_in.label" />
                        }
                        name="inProject"
                        valuePropName="checked"
                      >
                        <Switch
                          data-cy="views.new-view.view-available.task-lists"
                          checked={allowTaskListInProject}
                          onChange={onToggleViewInAllProject}
                          size="small"
                        />
                        <SwitchText>
                          <FormattedMessage id="views.view.task.list.projects" />
                          <ViewIcon>
                            <Catalog16 />
                          </ViewIcon>
                        </SwitchText>
                      </FormItemStyled>

                      <FormItemStyled
                        name="inCustomerPortal"
                        valuePropName="checked"
                      >
                        <Switch
                          data-cy="views.new-view.view-available.customer-portal"
                          checked={viewInCustomerPortal}
                          onChange={() =>
                            setViewInCustomerPortal(!viewInCustomerPortal)
                          }
                          size="small"
                          disabled={!allowTaskListInProject}
                        />
                        <CustomerPortalText
                          allowTaskListInProject={allowTaskListInProject}
                        >
                          <FormattedMessage id="views.view.customer.portal" />
                          <ViewIcon>
                            <ShareKnowledge16 />
                          </ViewIcon>
                        </CustomerPortalText>
                      </FormItemStyled>

                      {allowTaskListInProject && (
                        <FormItemWithoutMargin
                          label={
                            <FormattedMessage id="views.view.create.view" />
                          }
                          name="viewForAllProject"
                        >
                          <RadioGroup
                            onChange={(e) =>
                              setCreateViewForAllProject(e.target.value)
                            }
                          >
                            <Space direction="vertical">
                              <Radio
                                value
                                data-cy="views.new-view.create-view-for.all-projects"
                              >
                                <FormattedMessage id="views.view.create.view.all.projects" />
                              </Radio>
                              <Radio
                                value={false}
                                data-cy="views.new-view.create-view-for.match-conditions"
                              >
                                <FormattedMessage id="views.view.create.view.match.conditions" />
                              </Radio>
                            </Space>
                          </RadioGroup>
                        </FormItemWithoutMargin>
                      )}
                      {!createViewForAllProject && allowTaskListInProject ? (
                        <>
                          <FormItemConditions name="createView">
                            <FilterDrawerWithTrigger
                              fields={allFields}
                              onSubmit={setFilter}
                              filter={filter}
                            />
                          </FormItemConditions>
                        </>
                      ) : (
                        <></>
                      )}
                    </>
                  </Panel>
                </CollapseStyled>
              )}
          </FormStyled>
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <ButtonGroup $reversed>
            <SaveUpdateViewButton
              disabled={saveDisabled}
              onClick={onSave}
              loading={actionInProgress}
            >
              {editMode ? (
                <FormattedMessage id="update" />
              ) : (
                <FormattedMessage id="save" />
              )}
            </SaveUpdateViewButton>
            <CancelSaveViewButton onClick={onClose} />
          </ButtonGroup>
        </DrawerContent.Footer>
      </Content>
    </DrawerAtom>
  );
};
